/**
 * API client for Pulse — reads CloakAI JWT from localStorage.
 *
 * All requests (including login) go through the Pulse backend so
 * PULSE_EMAILS is always enforced server-side.
 *
 * On local Docker, Pulse is proxied at /pulse on the same origin.
 * On Railway, Pulse is a standalone service on its own domain (no /pulse
 * prefix). The Pulse backend proxies auth requests to the CloakAI API
 * using CLOAKAI_API_URL.
 */

// When VITE_API_URL is set (Railway), Pulse runs standalone — no /pulse prefix.
const _apiUrl = (import.meta.env.VITE_API_URL ?? "").toString().trim()
const STANDALONE = !!_apiUrl
const BASE = STANDALONE ? window.location.origin : window.location.origin + '/pulse'

// Callback invoked when any API call gets a 401 — set by App.tsx
let _onSessionExpired: (() => void) | null = null
export function setOnSessionExpired(cb: () => void) {
  _onSessionExpired = cb
}

function getToken(): string | null {
  return localStorage.getItem("cloakai_access_token")
}

function headers(): Record<string, string> {
  const h: Record<string, string> = { "Content-Type": "application/json" }
  const token = getToken()
  if (token) h["Authorization"] = `Bearer ${token}`
  return h
}

function handleResponse(r: Response): Response {
  if (r.status === 401 && _onSessionExpired) {
    _onSessionExpired()
  }
  return r
}

export function isLoggedIn(): boolean {
  return !!getToken()
}

export async function logout(): Promise<void> {
  // Call the signout endpoint via the Pulse backend proxy to revoke the token
  try {
    await fetch(`${BASE}/auth/signout`, {
      method: "POST",
      headers: headers(),
    })
  } catch {
    // Best-effort — clear tokens regardless
  }
  localStorage.removeItem("cloakai_access_token")
  localStorage.removeItem("cloakai_refresh_token")
  localStorage.removeItem("cloakai_active_job")
  localStorage.removeItem("cloakai_completed_job")
}

export async function login(email: string, password: string): Promise<{ ok: boolean; email?: string; error?: string }> {
  try {
    const r = await fetch(`${BASE}/auth/signin`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    })
    if (!r.ok) {
      const err = await r.json().catch(() => ({ detail: `HTTP ${r.status}` }))
      return { ok: false, error: err.detail || `Login failed (${r.status})` }
    }
    const data = await r.json()
    if (data.access_token) {
      localStorage.setItem("cloakai_access_token", data.access_token)
    }
    if (data.refresh_token) {
      localStorage.setItem("cloakai_refresh_token", data.refresh_token)
    }
    return { ok: true, email: data.user?.email || email }
  } catch (err: any) {
    return { ok: false, error: err.message || "Network error" }
  }
}

export async function checkAuth(): Promise<{ ok: boolean; email?: string; forbidden?: boolean }> {
  const token = getToken()
  if (!token) return { ok: false }

  const r = await fetch(`${BASE}/auth/check`, { headers: headers() })
  if (r.ok) return r.json()
  if (r.status === 403) return { ok: false, forbidden: true }
  return { ok: false }
}

export async function ask(question: string, sessionId?: string | null) {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), 120_000)
  try {
    const r = handleResponse(await fetch(`${BASE}/ask`, {
      method: "POST",
      headers: headers(),
      body: JSON.stringify({ question, session_id: sessionId }),
      signal: controller.signal,
    }))
    if (!r.ok) throw new Error(`HTTP ${r.status}`)
    return r.json()
  } catch (err: any) {
    if (err.name === "AbortError") throw new Error("Request timed out — the backend is running slow. Try again.")
    throw err
  } finally {
    clearTimeout(timeout)
  }
}

export async function getStatus() {
  const r = handleResponse(await fetch(`${BASE}/status`, { headers: headers() }))
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

export async function getWelcome(): Promise<{ message: string; status: string; alert_count: number }> {
  const r = handleResponse(await fetch(`${BASE}/welcome`, { headers: headers() }))
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

export async function getAlerts() {
  const r = await fetch(`${BASE}/alerts`, { headers: headers() })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

export async function acknowledgeAlert(id: number) {
  await fetch(`${BASE}/alerts/${id}/acknowledge`, { method: "POST", headers: headers() })
}

export async function resolveAlert(id: number) {
  await fetch(`${BASE}/alerts/${id}/resolve`, { method: "POST", headers: headers() })
}

export async function getCapabilities() {
  const r = await fetch(`${BASE}/capabilities`, { headers: headers() })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

export async function getSmsStatus(): Promise<{ configured: boolean; provider: string }> {
  const r = await fetch(`${BASE}/sms/status`, { headers: headers() })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

export async function testSms(): Promise<{ ok: boolean; message: string }> {
  const r = await fetch(`${BASE}/sms/test`, {
    method: "POST",
    headers: headers(),
  })
  if (!r.ok) {
    const err = await r.json().catch(() => ({ detail: `HTTP ${r.status}` }))
    throw new Error(err.detail || `HTTP ${r.status}`)
  }
  return r.json()
}

export async function saveReaction(sessionId: string, messageSnippet: string, reaction: string) {
  await fetch(`${BASE}/reactions`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ session_id: sessionId, message_snippet: messageSnippet, reaction }),
  })
}

export async function startParty(sessionId?: string | null): Promise<{ messages: Array<{ agent_id: string; agent_name: string; message: string }> }> {
  const r = handleResponse(await fetch(`${BASE}/party`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ session_id: sessionId }),
  }))
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

export async function partyReply(
  message: string,
  partyHistory: Array<{ agent_name: string; message: string }>,
  sessionId?: string | null,
): Promise<{ messages: Array<{ agent_id: string; agent_name: string; message: string }> }> {
  const r = handleResponse(await fetch(`${BASE}/party/reply`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ message, party_history: partyHistory, session_id: sessionId }),
  }))
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

// ── Autopilot ────────────────────────────────────────────────────────────

export interface AutopilotStatus {
  enabled: boolean
  enabled_at: number | null
  actions_taken: number
  recent_log: Array<{ id: number; action: string; detail: string; source_agent: string | null; created_at: string }>
  error?: string
}

export async function getAutopilotStatus(): Promise<AutopilotStatus> {
  const r = handleResponse(await fetch(`${BASE}/autopilot/status`, { headers: headers() }))
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

export async function toggleAutopilot(enable: boolean): Promise<AutopilotStatus> {
  const r = handleResponse(await fetch(`${BASE}/autopilot/toggle`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ enable }),
  }))
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

// ── Authority Tiers ────────────────────────────────────────────────────

export interface PendingAction {
  action_id: string
  action: string
  tier: number
  reason: string
  proposed_at: number
  veto_remaining_seconds: number
  ready: boolean
}

export interface AuthoritySummary {
  known_actions: number
  pending_tier2: number
  vetoed_total: number
  executed_total: number
  tier_counts: { tier1_actions: number; tier2_actions: number; tier3_actions: number }
}

export interface AuthorityStatus {
  summary: AuthoritySummary
  pending_actions: PendingAction[]
}

export async function getAuthorityStatus(): Promise<AuthorityStatus> {
  const r = handleResponse(await fetch(`${BASE}/authority/status`, { headers: headers() }))
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

export async function vetoAction(actionId: string, reason?: string): Promise<{ ok: boolean; action_id: string }> {
  const r = handleResponse(await fetch(`${BASE}/authority/veto/${actionId}`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ reason: reason || "Vetoed by operator" }),
  }))
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

// ── Generic fetch helpers (used by Dashboard, CommandTab, etc.) ──────────

/** GET from /pulse/{path} with auth headers, returns parsed JSON. */
export async function fetchPulse(path: string) {
  const r = handleResponse(await fetch(`${BASE}${path}`, { headers: headers() }))
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

/** GET from origin/{path} (non-pulse routes like /jobs, /analytics) with auth headers. */
export async function fetchOrigin(path: string) {
  const r = handleResponse(await fetch(`${window.location.origin}${path}`, { headers: headers() }))
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

/** POST to /pulse/{path} with optional JSON body, returns parsed JSON. */
export async function postPulse(path: string, body?: unknown) {
  const r = handleResponse(await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: headers(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  }))
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

// ── Dashboard / Command domain helpers ───────────────────────────────────

export async function getIncidents(): Promise<{ incidents: any[]; slo: any }> {
  return fetchPulse("/dashboard/incidents")
}

export interface GpuWorker {
  id: string
  gpu_name: string
  gpu_vram_mb: number
  alive: boolean
  ready: boolean
  gpu_utilization: number | null
  gpu_temp_celsius?: number | null
}

export interface StatusSnapshot {
  ts: number
  queue_depth: number
  queue_pub: number
  queue_pri: number
  queue_default: number
  worker_alive: boolean
  engine_ready: boolean
  gpu_pct: number | null
  gpu_peak: number | null
  gpu_temp: number | null
  active_jobs: number
  total_cloaks: number
  disk_pct: number | null
  alerts_critical: number
  alerts_warn: number
  alerts_total: number
  gpu_workers: GpuWorker[]
  gpu_workers_total: number
  gpu_workers_alive: number
  gpu_workers_ready: number
  throughput_jobs_per_min: number
  throughput_avg_s: number
}

export interface HealthCheckResult {
  overall: "healthy" | "degraded" | "critical"
  total_findings: number
  checks: Record<string, {
    health: string
    observations?: Record<string, number>
    findings?: Array<{ message: string; severity: string; detail: string }>
    error?: string
  }>
}

export async function getHealthChecks(): Promise<HealthCheckResult> {
  const r = handleResponse(await fetch(`${BASE}/health/checks`, { headers: headers() }))
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

// ── Command endpoint (single-screen hero card) ──────────────────────────

export interface CommandData {
  mode: "needs_you" | "issues" | "all_clear" | "shift_report"
  headline: string
  subtitle: string
  incident?: any
  command_center?: any
  shift_report?: any
  snapshot: {
    worker_alive: boolean
    engine_ready: boolean
    gpu_workers: GpuWorker[]
    gpu_workers_total: number
    gpu_workers_alive: number
    gpu_workers_ready: number
    queue_depth: number
    throughput_jobs_per_min: number
    throughput_avg_s: number
  }
  autopilot_recent: Array<{ action: string; detail: string; source: string; time: string }>
  jobs_total?: number
}

export async function getCommand(): Promise<CommandData> {
  return fetchPulse("/command")
}

export async function getStatusSnapshot(): Promise<StatusSnapshot> {
  return fetchPulse("/status/snapshot")
}

export async function getOpsKpis() {
  return fetchPulse("/dashboard/ops-kpis")
}

export async function getBriefing() {
  return fetchPulse("/briefing")
}

export async function getAutopilotLocks() {
  return fetchPulse("/autopilot/locks")
}

export async function getModerationOps() {
  return fetchPulse("/dashboard/moderation-ops")
}

export async function getBenchmarkDashboard() {
  return fetchPulse("/dashboard/benchmarks")
}

export async function getCommandCenter(incidentId: number) {
  return fetchPulse(`/dashboard/incidents/${incidentId}/command-center`)
}

export async function executeSuggestedRunbook(incidentId: number) {
  return postPulse(`/dashboard/incidents/${incidentId}/execute-suggested`)
}

export async function sendSmsCommand(text: string) {
  return postPulse("/sms/command", { text })
}

export async function unlockClass(alertClass: string) {
  return postPulse(`/autopilot/locks/${encodeURIComponent(alertClass)}/unlock`)
}

// ── Agent monitoring APIs ───────────────────────────────────────────────

export async function getAgentStatus() {
  return fetchPulse("/status/agents")
}

export async function getAgentDetail(agentId: string) {
  return fetchPulse(`/status/agents/${agentId}`)
}

export async function getDependencyGraph() {
  return fetchPulse("/status/graph")
}

export async function getSloStatus() {
  return fetchPulse("/status/slos")
}

export async function getFeedbackStatus() {
  return fetchPulse("/status/feedback")
}

export async function recordCorrection(data: {
  incident_id: string | number
  pulse_proposed: Record<string, unknown>
  operator_action: string
  notes?: string
  outcome?: string
}) {
  return postPulse("/status/agents/correction", data)
}

export async function recordManualResolve(data: {
  incident_id: string | number
  resolution: string
  notes?: string
}) {
  return postPulse("/status/agents/resolve-manual", data)
}

// ── Shift Report ──────────────────────────────────────────────────────

export async function getShiftReport(): Promise<any> {
  return fetchPulse("/shift-report")
}

export async function getShiftReportLatest(): Promise<any> {
  return fetchPulse("/shift-report/latest")
}

export async function getShiftReportHistory(): Promise<any[]> {
  return fetchPulse("/shift-report/history")
}

export async function generateShiftReport(): Promise<any> {
  return postPulse("/shift-report/generate")
}

// ── Phase 4: Intelligence Layer ──────────────────────────────────────

export async function getCircuitBreakers() {
  return fetchPulse("/circuit-breakers")
}

export async function resetCircuitBreaker(actionKey: string) {
  return postPulse(`/circuit-breakers/${encodeURIComponent(actionKey)}/reset`)
}

export async function getVerifications() {
  return fetchPulse("/verifications")
}

export async function getVerificationsPending() {
  return fetchPulse("/verifications/pending")
}

export async function getAmbientStatus() {
  return fetchPulse("/ambient")
}

export async function enterAmbientMode(agentId: string) {
  return postPulse(`/ambient/${encodeURIComponent(agentId)}/enter`)
}

export async function exitAmbientMode(agentId: string) {
  return postPulse(`/ambient/${encodeURIComponent(agentId)}/exit`)
}

export async function getLearningStatus() {
  return fetchPulse("/learning")
}

export async function getLearningCorrections() {
  return fetchPulse("/learning/corrections")
}

export async function getLearningPatterns() {
  return fetchPulse("/learning/patterns")
}

export async function submitCorrection(data: {
  pulse_action: string
  operator_action: string
  pulse_runbook?: string
  pulse_confidence?: number
  operator_notes?: string
  incident_type?: string
  source_agent?: string
  alert_message?: string
}) {
  return postPulse("/learning/corrections", data)
}

export async function dryRunRunbook(runbookId: string) {
  return postPulse(`/runbooks/${encodeURIComponent(runbookId)}/dry-run`)
}

// ── Phase 5: Creator Intelligence ────────────────────────────────────

export async function getCreatorReport(userId: string) {
  return fetchPulse(`/creator/${encodeURIComponent(userId)}/report`)
}

export async function getCreatorHealth(userId: string) {
  return fetchPulse(`/creator/${encodeURIComponent(userId)}/health`)
}

export async function getCreatorThreats(userId: string) {
  return fetchPulse(`/creator/${encodeURIComponent(userId)}/threats`)
}

export async function askCreator(userId: string, question: string) {
  return postPulse(`/creator/${encodeURIComponent(userId)}/ask`, { question })
}

export async function getThreatFeed() {
  return fetchPulse("/threats")
}

// ── Launch Readiness Checks ──────────────────────────────────────────────

export interface CheckResult {
  name: string
  status: "pass" | "fail" | "warn" | "skip"
  duration_ms: number
  detail: string
  error: string
}

export interface CheckSuite {
  id: string
  name: string
  checks: CheckResult[]
}

export interface ChecksResponse {
  overall: "pass" | "fail" | "warn"
  total_pass: number
  total_fail: number
  total_warn: number
  total_skip: number
  total_checks: number
  duration_ms: number
  timestamp: number
  environment: string
  api_url: string
  suites: CheckSuite[]
}

export async function runAllChecks(): Promise<ChecksResponse> {
  return fetchPulse("/checks/run")
}

export async function runCheckSuite(suiteId: string): Promise<CheckSuite & { duration_ms: number }> {
  return fetchPulse(`/checks/run/${encodeURIComponent(suiteId)}`)
}

// ── Diagnostics ──────────────────────────────────────────────────────────

export async function getDiagnosis(): Promise<{
  severity: string
  analysis: string
  actions: Array<{ label: string; detail?: string; auto_fixable?: boolean; action_key?: string }>
}> {
  return fetchPulse("/diagnose")
}

export async function getCreatorStats() {
  return fetchPulse("/dashboard/creator-stats")
}

// ── Metrics History (replaces Prometheus/Grafana) ──────────────────────

export interface MetricsPoint {
  ts: string
  queue_total: number | null
  queue_publisher: number | null
  queue_priority: number | null
  queue_default: number | null
  worker_alive: boolean | null
  engine_ready: boolean | null
  gpu_util_pct: number | null
  gpu_peak_pct: number | null
  gpu_temp_c: number | null
  total_cloaks: number | null
  active_jobs: number | null
  jobs_per_min: number | null
  avg_proc_s: number | null
  disk_used_gb: number | null
  disk_total_gb: number | null
  disk_pct: number | null
  gpu_workers_total: number | null
  gpu_workers_alive: number | null
  gpu_workers_ready: number | null
  active_clients: number | null
  active_24h: number | null
}

export interface MetricsHistoryResponse {
  points: MetricsPoint[]
  minutes: number
  count: number
}

export async function getMetricsHistory(minutes: number = 60): Promise<MetricsHistoryResponse> {
  return fetchPulse(`/dashboard/metrics-history?minutes=${minutes}`)
}

// ── Feed (unified activity timeline) ─────────────────────────────────────

export interface FeedEntry {
  ts: string
  type: "alert" | "autopilot" | "job" | "slo" | "system"
  icon: string
  severity: string
  message: string
  source: string
  id?: number
}

export interface FeedResponse {
  entries: FeedEntry[]
  count: number
}

export async function getFeed(limit: number = 50): Promise<FeedResponse> {
  return fetchPulse(`/feed?limit=${limit}`)
}

// ── Ops: Queue Management ────────────────────────────────────────────────

export interface QueueJob {
  id: string
  status: string
  queue: string
  customer_id?: string
  created_at?: string
  error?: string
}

export interface QueueData {
  jobs: QueueJob[]
  stats: { publisher: number; priority: number; default: number; total: number }
}

export async function getQueue(opts?: { status?: string; user?: string }): Promise<QueueData> {
  const params = new URLSearchParams()
  if (opts?.status) params.set("status", opts.status)
  if (opts?.user) params.set("user", opts.user)
  const qs = params.toString()
  return fetchPulse(`/ops/queue${qs ? `?${qs}` : ""}`)
}

export async function clearQueue(mode: "all" | "user" | "job", target?: string): Promise<{ cleared: number }> {
  return postPulse("/ops/queue/clear", { mode, target })
}

// ── Ops: Service Management ──────────────────────────────────────────────

export interface ServiceInfo {
  name: string
  status: string
  detail?: string
  source: string
}

export async function getServices(): Promise<{ services: ServiceInfo[]; deployment_mode: string }> {
  return fetchPulse("/ops/services")
}

export async function restartService(name: string): Promise<{ success: boolean; detail: string }> {
  return postPulse(`/ops/services/${name}/restart`)
}

// ── Ops: API Tester ──────────────────────────────────────────────────────

export interface ApiTestResult {
  status: number
  elapsed_ms: number
  headers: Record<string, string>
  body: any
  error: string | null
}

export async function testApi(method: string, path: string, body?: any): Promise<ApiTestResult> {
  return postPulse("/api-test", { method, path, body })
}

// ── SLOs ──────────────────────────────────────────────────────────────────

export interface SloStatus {
  slo_name: string
  current_value: number | null
  target: number
  met: boolean
  budget_remaining: number
  burn_rate: number
  severity: "ok" | "watch" | "investigate" | "emergency"
  message: string
}

export interface SloData {
  slos: Record<string, SloStatus>
  escalation: string
}

export async function getSlos(): Promise<SloData> {
  return fetchPulse("/status/slos")
}

// ── Runbooks ──────────────────────────────────────────────────────────────

export interface RunbookInfo {
  id: string
  name: string
  description: string
  trigger: string
  authority_tier: number
  authority_label: string
  steps: string[]
  cooldown_seconds: number
  can_execute: boolean
  last_executed: number | null
  danger: "safe" | "caution" | "dangerous"
}

export async function getRunbooks(): Promise<{ runbooks: RunbookInfo[] }> {
  return fetchPulse("/runbooks")
}

export async function executeRunbook(runbookId: string): Promise<{ success: boolean; detail?: string; error?: string }> {
  return postPulse(`/runbooks/${encodeURIComponent(runbookId)}/execute`)
}
